/**
 * SEO Schema generation utilities for Elevation Electrical
 * Generates JSON-LD structured data for various schema types
 */

import { IMAGE_PATHS } from './imagePaths';
import { COMPANY_INFO } from '../constants';
import { GOOGLE_REVIEW_STATS } from '../constants/googleReviews';

export interface ServiceSchemaProps {
  name: string;
  serviceType: string;
  description?: string;
  url?: string;
}

export interface FAQItem {
  question: string;
  answer: string;
}

/**
 * Generates Service JSON-LD schema
 */
export const generateServiceSchema = (props: ServiceSchemaProps) => {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": props.name,
    "serviceType": props.serviceType,
    "description": props.description,
    "url": props.url,
    "areaServed": [
      { "@type": "City", "name": "Lake Helen, FL" },
      { "@type": "City", "name": "DeLand, FL" },
      { "@type": "City", "name": "Deltona, FL" },
      { "@type": "City", "name": "Orlando, FL" },
      { "@type": "City", "name": "Daytona Beach, FL" },
      { "@type": "City", "name": "Sanford, FL" }
    ],
    "provider": {
      "@id": "https://www.elevationelectricalfl.com/#business"
    }
  };
};

/**
 * Generates EV Charging-specific Service schema with enhanced detail
 */
export const generateEVChargingSchema = () => {
  return [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "EV Charging Station Installation",
      "serviceType": "Electric Vehicle Charging Station Installation",
      "description": "Professional Level 2 EV charging station installation for homes and businesses in Central Florida. Tesla Wall Connector, ChargePoint, and universal NEMA 14-50 installations. Licensed electrician EC13011028.",
      "url": "https://www.elevationelectricalfl.com/services/ev-charging",
      "provider": { "@id": "https://www.elevationelectricalfl.com/#business" },
      "areaServed": [
        { "@type": "AdministrativeArea", "name": "Volusia County, FL" },
        { "@type": "AdministrativeArea", "name": "Seminole County, FL" },
        { "@type": "AdministrativeArea", "name": "Orange County, FL" }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "EV Charging Installation Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Level 2 Home EV Charger Installation",
              "description": "240V dedicated circuit and wall-mounted charger installation for residential garages"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Tesla Wall Connector Installation",
              "description": "Certified Tesla Wall Connector installation with dedicated 60A circuit"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Commercial EV Charging Station Installation",
              "description": "Multi-unit charging station installation for businesses, apartments, and public spaces"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Electrical Panel Upgrade for EV Charging",
              "description": "200A or 400A panel upgrade to support Level 2 EV charging requirements"
            }
          }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How much does it cost to install a Level 2 EV charger in Florida?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Level 2 EV charger installation in Central Florida typically costs between $1,200 and $3,500 including labor, materials, and permitting. Costs vary based on panel capacity, wire run distance, and charger model. Panel upgrades, if needed, add $2,000–$6,000."
          }
        },
        {
          "@type": "Question",
          "name": "Do I need a panel upgrade to install an EV charger?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Many Florida homes built before 2000 have 100A or 150A panels that may need upgrading to 200A to support a Level 2 EV charger (40–60A circuit). Our licensed electricians perform a load analysis during the free assessment to determine if an upgrade is necessary."
          }
        },
        {
          "@type": "Question",
          "name": "Can you install a Tesla Wall Connector?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Elevation Electrical installs Tesla Wall Connectors, Tesla Universal Wall Connectors (compatible with all EVs via J1772), and all major charger brands including ChargePoint, JuiceBox, Grizzl-E, and ClipperCreek."
          }
        },
        {
          "@type": "Question",
          "name": "Are there tax credits or rebates for EV charger installation in Florida?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. The federal Alternative Fuel Vehicle Refueling Property Credit (IRC 30C) provides up to $1,000 for residential and $100,000 for commercial installations through 2032. Some Florida utilities also offer rebates. We provide guidance on available incentives during your consultation."
          }
        },
        {
          "@type": "Question",
          "name": "How long does EV charger installation take?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Standard Level 2 installations typically take 3–5 hours. If a panel upgrade is needed, allow 1–2 additional days. We handle all permitting and inspections for Volusia, Seminole, and Orange counties."
          }
        }
      ]
    }
  ];
};

/**
 * Generates Generator-specific Service schema with enhanced detail
 */
export const generateGeneratorSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Whole Home Generator Installation",
    "name": "Standby Generator Installation",
    "description": "Professional whole-house standby generator installation for hurricane backup power in Volusia County. Generac, Kohler, and Cummins experts – licensed EC13011028.",
    "url": "https://www.elevationelectricalfl.com/services/generators",
    "provider": { "@id": "https://www.elevationelectricalfl.com/#business" },
    "areaServed": [
      { "@type": "AdministrativeArea", "name": "Volusia County, FL" },
      { "@type": "AdministrativeArea", "name": "Seminole County, FL" },
      { "@type": "AdministrativeArea", "name": "Orange County, FL" }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Generator Installation Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Residential Standby Generator (14–22 kW)",
            "description": "Whole-home backup generator with automatic transfer switch for standard Florida homes"
          },
          "priceSpecification": { "@type": "PriceSpecification", "priceCurrency": "USD", "minPrice": "7000", "maxPrice": "13000" }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Large Home Generator (24–48 kW)",
            "description": "High-capacity generator for large homes, estates, and properties with pools"
          },
          "priceSpecification": { "@type": "PriceSpecification", "priceCurrency": "USD", "minPrice": "12000", "maxPrice": "25000" }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Commercial Generator Installation",
            "description": "Commercial standby generator systems for businesses and multi-unit properties"
          }
        }
      ]
    }
  };
};

/**
 * Generates FAQPage JSON-LD schema
 */
export const generateFAQSchema = (faqs: FAQItem[]) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

/**
 * Generates Electrician entity schema (homepage only)
 */
export const generateElectricianSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Electrician",
    "@id": "https://www.elevationelectricalfl.com/#business",
    "name": "Elevation Electrical, LLC",
    "url": "https://www.elevationelectricalfl.com/",
    "email": "sales@elevationelectricalfl.com",
    "telephone": "+1-407-970-7211",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Lake Helen",
      "addressRegion": "FL",
      "addressCountry": "US"
    },
    "logo": IMAGE_PATHS["996141e7-ff11-4b49-9773-915b594801ff.png"],
    "image": IMAGE_PATHS["996141e7-ff11-4b49-9773-915b594801ff.png"],
    "description": "Licensed electrical contractor EC13011028 serving Volusia, Seminole, Orange, and Lake-Sumter counties. Specializing in EV charging station installation, generator installations, panel upgrades, residential and commercial electrical services. Family-owned, 470+ happy customers across Central Florida.",
    "knowsAbout": [
      "EV Charging Station Installation",
      "Tesla Wall Connector Installation",
      "Level 2 EV Charger Installation",
      "Electrical Panel Upgrades",
      "Generator Installation",
      "Residential Electrical Services",
      "Commercial Electrical Services",
      "Electrical Safety Inspections",
      "New Construction Wiring",
      "Designer Lighting Installation"
    ],
    "areaServed": [
      { "@type": "AdministrativeArea", "name": "Volusia County, FL" },
      { "@type": "AdministrativeArea", "name": "Seminole County, FL" },
      { "@type": "AdministrativeArea", "name": "Orange County, FL" },
      { "@type": "City", "name": "Lake Helen, FL" },
      { "@type": "City", "name": "Orlando, FL" },
      { "@type": "City", "name": "Daytona Beach, FL" },
      { "@type": "City", "name": "Deltona, FL" },
      { "@type": "City", "name": "Sanford, FL" },
      { "@type": "City", "name": "DeLand, FL" },
      { "@type": "City", "name": "Winter Park, FL" },
      { "@type": "City", "name": "Lake Mary, FL" },
      { "@type": "City", "name": "Ormond Beach, FL" },
      { "@type": "City", "name": "New Smyrna Beach, FL" },
      { "@type": "City", "name": "Port Orange, FL" },
      { "@type": "City", "name": "The Villages, FL" }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": GOOGLE_REVIEW_STATS.ratingValue.toFixed(1),
      "reviewCount": String(GOOGLE_REVIEW_STATS.totalReviews),
      "bestRating": String(GOOGLE_REVIEW_STATS.bestRating),
      "worstRating": String(GOOGLE_REVIEW_STATS.worstRating)
    },
    "serviceType": [
      "Electrical Services",
      "EV Charging Station Installation",
      "Generator Installation",
      "Electrical Panel Upgrades"
    ],
    "priceRange": "$$",
    "openingHours": "Mo-Fr 08:00-17:00",
    "hasCredential": {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "license",
      "name": "Florida Electrical Contractor License EC13011028",
      "recognizedBy": {
        "@type": "Organization",
        "name": "State of Florida Department of Business and Professional Regulation"
      }
    },
    "founder": {
      "@type": "Person",
      "name": "Keith Stephenson"
    },
    "foundingDate": "2021",
    "sameAs": [
      "https://www.facebook.com/profile.php?id=100089725775324",
      "https://share.google/pi6PDA0LyFAXdnptb"
    ]
  };
};

/**
 * Generates city-specific LocalBusiness schema
 */
export const generateCitySchema = (cityName: string, cityState: string = "FL") => {
  return {
    "@context": "https://schema.org",
    "@type": "Electrician",
    "name": `Elevation Electrical, LLC - ${cityName} Electrician`,
    "url": `https://www.elevationelectricalfl.com/locations/${cityName.toLowerCase().replace(/\s+/g, '-')}`,
    "telephone": "+1-407-970-7211",
    "email": "sales@elevationelectricalfl.com",
    "priceRange": "$$",
    "description": `Licensed electrician EC13011028 serving ${cityName}, ${cityState}. EV charging station installation, panel upgrades, generators, residential and commercial electrical services.`,
    "areaServed": {
      "@type": "City",
      "name": `${cityName}, ${cityState}`
    },
    "parentOrganization": {
      "@id": "https://www.elevationelectricalfl.com/#business"
    },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Lake Helen",
      "addressRegion": "FL",
      "addressCountry": "US"
    },
    "serviceType": [
      "Electrical Services",
      "EV Charging Station Installation",
      "Generator Installation",
      "Electrical Panel Upgrades"
    ],
    "hasCredential": {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "license",
      "name": "Florida Electrical Contractor License EC13011028",
      "recognizedBy": {
        "@type": "Organization",
        "name": "State of Florida Department of Business and Professional Regulation"
      }
    }
  };
};
